import { BAND_HEIGHTS } from '../core/profile/cssVars';
import { suppressActivations } from '../core/input/activationRegistry';
import { useComposerStore } from '../core/composer/useComposer';
import { de } from '../i18n/de';
import { ActivateButton } from './ActivateButton';
import { ContentBand } from './ContentBand';
import { offerUndoClear } from './UndoClearOffer';

/**
 * How long activations of the bar are swallowed after a hold has fired.
 *
 * Long enough to cover a slow release and the synthetic click behind it, short enough that a person who
 * deliberately taps the bar straight afterwards is not ignored.
 */
const SUPPRESS_AFTER_HOLD_MS = 750;

/**
 * What the person is saying, and the largest element on the screen.
 *
 * **Centred, not left-aligned, and this is a mitigation rather than a style choice.** Under left
 * neglect a left-aligned block is a trap: the person may simply never read the beginning of their
 * own sentence. Centring plus keeping the last lines visible means the newest word is always near
 * the middle of the screen, where central vision is usable and where neither a left nor a right
 * field loss hides it (§5.2).
 *
 * Inside the content band, because reading your own text is a search task.
 *
 * ── Two gestures, one target (`SUR-25`, `SUR-26`, ADR-043) ──
 *
 * **Tap.** On Sprechen it opens Partner-Ansicht — the largest target in the app, and the gesture reads as
 * "show this to them". On Sätze and Notfall it goes to the keyboard instead, because a person who taps the
 * sentence *there* has just read a short label, found it says less than they meant, and wants to write; what
 * they used to get was the partner view showing «ich schreibe…» about an empty buffer. The destination names
 * itself, the way the bottom row's `Sätze` slot already does (ADR-010).
 *
 * **Hold.** Two seconds, still, clears the buffer — and only on Sprechen, because the band that offers the
 * sentence back is only on Sprechen and a destructive gesture whose recovery cannot be shown is not shipped.
 * This is not a split target: it is the same area, held longer, so `SUR-20`'s arithmetic is untouched and
 * ADR-041's promise that the bar stays one activation target still holds.
 *
 * The release that ends a hold would otherwise also activate — `DirectTouch` fires on release within target,
 * whatever the duration — so the hold suppresses this id for a moment. That belongs in the registry rather
 * than in a `stopPropagation` here; `useHoldGesture.ts` explains why at length.
 */

interface OutputBarProps {
  onActivate: () => void;
  /** Where a tap goes, which decides the accessible name and whether the hold is attached at all. */
  destination: 'partner' | 'keyboard';
}

export function OutputBar({ onActivate, destination }: OutputBarProps): React.JSX.Element {
  const state = useComposerStore((s) => s.state);
  const text = state.text;
  const clear = useComposerStore((s) => s.clear);

  const holdToClear = (): void => {
    if (text === '') return;
    // Swallow both arrivals of the release that ends this hold: the pointer adapter's and the synthetic
    // click, which can land outside the 25 ms coalesce window.
    suppressActivations('output:show-partner', SUPPRESS_AFTER_HOLD_MS);
    offerUndoClear(text);
    clear();
  };

  return (
    <ContentBand>
      <ActivateButton
        id="output:show-partner"
        onActivate={onActivate}
        testId="output-bar"
        label={destination === 'partner' ? de.showText : de.showKeyboard}
        {...(destination === 'partner' ? { hold: { onHold: holdToClear } } : {})}
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          width: '100%',
          height: BAND_HEIGHTS.output,
          minHeight: 0,
          padding: '4px 12px',
          background: 'var(--band-bg)',
          border: '3px solid var(--key-border)',
          borderRadius: 'var(--radius)',
          // The band is only 60 % of the width, so at 48 px a line holds roughly a dozen
          // characters. Overflow scrolls the *end* into view rather than clipping the start,
          // which is why alignItems is flex-end.
          overflow: 'hidden',
        }}
      >
        <span
          data-testid="output-text"
          style={{
            display: 'block',
            width: '100%',
            fontSize: 'var(--font-output)',
            lineHeight: 1.15,
            textAlign: 'center',
            overflowWrap: 'break-word',
            // Preserve the trailing space the person just typed: without this, a word boundary
            // vanishes visually and the sentence looks like it lost a keystroke.
            whiteSpace: 'pre-wrap',
          }}
        >
          {text}
        </span>
      </ActivateButton>
    </ContentBand>
  );
}
